/**
 * The prerequisites AFAR Connect Computer Setup puts on a remote machine, and
 * the small shell it needs to read its own files afterwards.
 *
 * There are no prerequisites left to install. The Environment Service Executable carries its own
 * runtime, so the ~50 MB Node download and the npm install that needed it are gone. What remains
 * is a platform check and the shell helpers the install itself uses.
 *
 * Losing Node also loses `node -e`, which is what used to read the service's own
 * JSON. Those reads are `afar_json_*` below: they run against files this project
 * writes, in a shell that is always present.
 */
import { unamePlatformCaseScript } from "./environmentServiceUname.ts";

/** Versioned upstream artifacts, checked before extraction or execution. */
export const quoteComputerShell = (value: string) => `'${value.replaceAll("'", "'\\''")}'`;

/** The platforms an Environment Service Executable is published for. */
const SUPPORTED_PLATFORMS = ["darwin-arm64", "darwin-x64", "linux-x64"] as const;

/**
 * Read one string field out of a JSON object this project wrote.
 *
 * `afar_json_string <file> <key>` prints the value or nothing. Scoped by
 * compacting the document and splitting it into one line per object, so a field
 * is read out of the object that carries it rather than out of whatever came
 * first in the file.
 */
export const computerJsonShellFunctions = [
  'afar_json_compact() { tr -d "[:space:]" < "$1" | tr "{}" "\\n\\n"; }',
  'afar_json_string() { afar_json_compact "$1" | grep -o "\\"$2\\":\\"[^\\"]*\\"" | head -n 1 | cut -d "\\"" -f 4; }',
  'afar_json_number() { afar_json_compact "$1" | grep -o "\\"$2\\":[0-9][0-9]*" | head -n 1 | cut -d ":" -f 2; }',
  // The object that carries `$2`:`$3` also carries `$4`; find it, then read it.
  'afar_json_sibling() { afar_json_compact "$1" | grep -F "\\"$2\\":\\"$3\\"" | grep -o "\\"$4\\":\\"[^\\"]*\\"" | head -n 1 | cut -d "\\"" -f 4; }',
].join("\n");

/**
 * Stop the Environment Service and leave nothing of it behind.
 *
 * Setup replaces a computer's whole installation, so it has to be able to
 * proceed from any state a previous attempt left — including one where the
 * service no longer answers its own stop request and keeps holding the store
 * and its old allocation. A stale service that cannot be unlinked is what makes
 * the next link produce an endpoint nobody is serving.
 *
 * The only process this will ever signal is the one the service recorded as its
 * own, and only while that PID still is the executable that recorded it. Never
 * a pattern match: this computer runs its owner's processes too, and a name can
 * belong to any of them.
 */
export const computerForceStopShellFunction = [
  "afar_force_stop() {",
  '  "$afar_cli" stop --base-dir "$afar_store" </dev/null >/dev/null 2>&1 || true',
  '  afar_evidence="$afar_store/runtime/environment/server-runtime.json"',
  '  [ -f "$afar_evidence" ] || return 0',
  '  afar_pid=$(afar_json_number "$afar_evidence" pid)',
  '  if [ -z "$afar_pid" ]; then rm -f "$afar_evidence"; return 0; fi',
  '  case "$(ps -o comm= -p "$afar_pid" 2>/dev/null)" in',
  "    *afar-research*) ;;",
  '    *) rm -f "$afar_evidence"; return 0 ;;',
  "  esac",
  '  echo "Stopping the Environment Service still running as pid $afar_pid." >&2',
  '  kill -TERM "$afar_pid" 2>/dev/null || true',
  "  afar_waited=0",
  '  while [ "$afar_waited" -lt 20 ] && kill -0 "$afar_pid" 2>/dev/null; do',
  "    sleep 1",
  "    afar_waited=$((afar_waited + 1))",
  "  done",
  '  if kill -0 "$afar_pid" 2>/dev/null; then',
  '    echo "It did not stop; ending it." >&2',
  '    kill -KILL "$afar_pid" 2>/dev/null || true',
  "    sleep 1",
  "  fi",
  '  rm -f "$afar_evidence"',
  "}",
].join("\n");

export function computerBootstrapScript() {
  return [
    "set -eu",
    "set +x",
    "umask 077",
    'afar_tools="$HOME/.local/share/afar-computer/tools"',
    'mkdir -p "$afar_tools"',
    unamePlatformCaseScript("afar_platform"),
    // Refused here rather than at download: a machine with no published executable cannot be
    // installed onto, and saying so before anything is fetched costs the person nothing.
    `case "$afar_platform" in ${SUPPORTED_PLATFORMS.join("|")}) ;;`,
    '*) echo "Unsupported computer platform." >&2; exit 1 ;;',
    "esac",
    'afar_verify() { if command -v shasum >/dev/null; then afar_digest=$(shasum -a 256 "$1"); elif command -v sha256sum >/dev/null; then afar_digest=$(sha256sum "$1"); else echo "A SHA-256 tool is required." >&2; return 1; fi; [ "${afar_digest%% *}" = "$2" ] || { echo "Prerequisite checksum mismatch." >&2; return 1; }; }',
    'afar_download() { curl --fail --silent --show-error --location --proto "=https" --proto-redir "=https" --connect-timeout 15 --max-time 180 --retry 2 "$1" -o "$2"; afar_verify "$2" "$3"; }',
    computerJsonShellFunctions,
  ].join("\n");
}
